import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { request } from "utils/request";
import { REACT_APP_API_URL } from "utils/environmentVariables";
import { toast } from "react-toastify";
import {
  addUserLeader,
  getDepartments,
  getTeamAndDepartmentLeads,
  removeUserLeader,
} from "app/containers/AdminConsole/slice";
import {
  deleteUserByUserAccountId,
  getAllTeamsByCompanyAccountId,
} from "app/containers/Global/slice";
import {
  UpdateOrganizationalStructurePayload,
  UpdateOrganizationalStructureResponse,
} from "./types";
import { successNotificationOptions } from "utils/constants";
import { responseStatus } from "utils/types";
import { RootState } from "utils/redux/store";
import { getSuccessToastMessageFromPayload } from "./helpers";

// ------------------ State Type/Structure ------------------
export interface DeactivateUserModalState {
  updateOrganizationalStructureResponse: UpdateOrganizationalStructureResponse | null;
  deleteUserByUserAccountIdWithExtraStepsStatus: responseStatus;
}

// ------------------ InitialState ------------------
const initialState: DeactivateUserModalState = {
  updateOrganizationalStructureResponse: null,
  deleteUserByUserAccountIdWithExtraStepsStatus: "idle",
};

export const deleteUserByUserAccountIdWithExtraSteps = createAsyncThunk(
  "deleteUserByUserAccountIdWithExtraSteps",
  async (
    {
      payload,
      userToDeactivate,
    }: {
      payload: UpdateOrganizationalStructurePayload;
      userToDeactivate: number;
    },
    { dispatch, rejectWithValue, getState }
  ) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/organizationalChanges`;
    const response = (await request(
      requestUrl,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
      true
    )) as UpdateOrganizationalStructureResponse & { status: number };

    if (response.status !== 200) {
      toast.error(
        "Looks like something went wrong. Fix errors and try again.",
        {
          ...successNotificationOptions,
          className: "error",
        }
      );
      return rejectWithValue(response);
    }

    await dispatch(deleteUserByUserAccountId(userToDeactivate));

    toast.success(
      getSuccessToastMessageFromPayload(payload),
      successNotificationOptions
    );

    if (payload.departmentChanges) {
      payload.departmentChanges.forEach((departmentChange) => {
        dispatch(
          removeUserLeader({
            type: "departments",
            id: departmentChange.departmentId,
            userAccountId: userToDeactivate,
          })
        );

        if (departmentChange.newDepartmentLeaderUserAccountId) {
          dispatch(
            addUserLeader({
              type: "departments",
              id: departmentChange.departmentId,
              userAccountId: departmentChange.newDepartmentLeaderUserAccountId,
            })
          );
        }
      });
    }

    const {
      global: { companyInfo },
    } = getState() as RootState;

    // If there are any team changes, we need to refetch all teams
    if (payload.teamChanges) {
      dispatch(
        getAllTeamsByCompanyAccountId({
          companyAccountId: companyInfo?.companyAccountId ?? 0,
          refreshTeams: true,
        })
      );
    }

    // If there are any department changes or team changes, we need to refetch all team and department leads
    if (payload.departmentChanges || payload.teamChanges) {
      dispatch(getTeamAndDepartmentLeads({ forceFetch: true }));
      dispatch(
        getDepartments({
          companyAccountId: companyInfo?.companyAccountId ?? 0,
          forceFetch: true,
        })
      );
    }

    return response;
  }
);

export const deactivateUserModalSlice = createSlice({
  name: "deactivateUserModal",
  initialState,
  reducers: {
    clearDeactivateUserModalState: (state) => {
      state.updateOrganizationalStructureResponse = null;
      state.deleteUserByUserAccountIdWithExtraStepsStatus = "idle";
    },
    clearUpdateOrganizationalStructureResponseError: (
      state,
      {
        payload,
      }: PayloadAction<{
        type: "department-changes" | "team-changes" | "manager-changes";
        id: number;
      }>
    ) => {
      if (!state.updateOrganizationalStructureResponse) return state;

      const currentResponse = state.updateOrganizationalStructureResponse;
      switch (payload.type) {
        case "department-changes": {
          const currentDepartmentChangesResponse =
            currentResponse.departmentChanges;
          delete currentDepartmentChangesResponse?.[payload.id];
          state.updateOrganizationalStructureResponse = {
            ...currentResponse,
            departmentChanges: currentDepartmentChangesResponse,
          };
          break;
        }
        case "team-changes": {
          const currentTeamChangesResponse = currentResponse.teamChanges;
          delete currentTeamChangesResponse?.[payload.id];
          state.updateOrganizationalStructureResponse = {
            ...currentResponse,
            teamChanges: currentTeamChangesResponse,
          };
          break;
        }
        case "manager-changes": {
          state.updateOrganizationalStructureResponse = {
            ...currentResponse,
            managerChanges: undefined,
          };
          break;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(deleteUserByUserAccountIdWithExtraSteps.pending, (state) => {
        state.deleteUserByUserAccountIdWithExtraStepsStatus = "loading";
      })
      .addCase(
        deleteUserByUserAccountIdWithExtraSteps.fulfilled,
        (state, action) => {
          state.deleteUserByUserAccountIdWithExtraStepsStatus = "succeeded";
          state.updateOrganizationalStructureResponse = action.payload;
        }
      )
      .addCase(
        deleteUserByUserAccountIdWithExtraSteps.rejected,
        (state, { payload }) => {
          state.deleteUserByUserAccountIdWithExtraStepsStatus = "failed";
          state.updateOrganizationalStructureResponse =
            payload as UpdateOrganizationalStructureResponse;
        }
      );
  },
});
// --------Selector--------
export const selectUpdateOrganizationalStructureResponse = (state: RootState) =>
  state.deactivateUserModal.updateOrganizationalStructureResponse;
export const selectDeleteUserByUserAccountIdWithExtraStepsStatus = (
  state: RootState
) => state.deactivateUserModal.deleteUserByUserAccountIdWithExtraStepsStatus;

export const { clearDeactivateUserModalState } =
  deactivateUserModalSlice.actions;

export default deactivateUserModalSlice.reducer;
